import React, { useState, useEffect } from "react";
import { 
  Phone, 
  MessageSquare, 
  MapPin, 
  Heart, 
  Award, 
  CheckCircle, 
  Clock, 
  ShieldAlert,
  Home as HomeIcon,
  ShieldCheck,
  PawPrint,
  Microscope,
  Send, 
  X,
  Activity,
  Menu,
  ExternalLink,
  Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "@/components/ui/sonner";
import { HOME } from "@/constants/testIds";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HOME_FAQS } from "@/data/homeFaqs";
import { Seo, faqSchema } from "@/components/Seo";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://ganges-pathology.preview.emergentagent.com";
const API = `${BACKEND_URL}/api`;

function Home() {
  // Mobile Nav menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Admin Dashboard state
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [loadingInquiries, setLoadingInquiries] = useState(false);

  // Auto test connection on mount
  useEffect(() => {
    const testApi = async () => {
      try {
        await axios.get(`${API}/`);
        console.log("Connected to Animal Analytics Backend successfully.");
      } catch (e) {
        console.error("Backend unreachable. Checking connection...", e);
      }
    };
    testApi();
  }, []);

  // Unlock Dashboard handler
  const handleUnlockDashboard = async (e) => {
    e.preventDefault();
    if (!passcode) {
      toast.error("Please enter the secure passcode.");
      return;
    }
    
    setLoadingInquiries(true);
    try {
      const response = await axios.get(`${API}/inquiries?passcode=${passcode}`);
      if (response.data && response.data.length >= 0) {
        if (passcode === "900718") {
          setIsUnlocked(true);
          setInquiries(response.data);
          toast.success("Welcome. Inquiries loaded successfully.");
        } else {
          toast.error("Incorrect passcode. Please check your admin credentials.");
        }
      }
    } catch (err) {
      toast.error("Error unlocking dashboard. Please verify connection.");
    } finally {
      setLoadingInquiries(false);
    }
  };

  // Close Dashboard
  const handleCloseDashboard = () => {
    setShowAdminPanel(false);
    setIsUnlocked(false);
    setPasscode("");
  };

  const navLinks = [
    { name: "Services", href: "#services", testId: HOME.navServices },
    { name: "Pets", href: "#animals", testId: HOME.navAnimals },
    { name: "Areas", href: "#reach", testId: HOME.navReach },
    { name: "Guides", href: "/blog", testId: "nav-guides-link" },
    { name: "FAQ", href: "#faq", testId: HOME.navFaq },
    { name: "Contact", href: "#contact", testId: HOME.navContact },
  ];

  return (
    <div className="min-h-screen flex flex-col font-sans antialiased text-[#2D2A26] bg-[#FAF6EF]">
      <Toaster position="top-center" closeButton />
      <Seo
        title="Best & Most Trusted Pet Pathology Lab in Kolkata | Dog & Cat Blood Tests | Animal Analytics"
        description="Top-rated pet pathology lab in Kolkata & West Bengal since 2012. Accurate blood tests, urine tests, stool tests & full diagnostics for dogs, cats and all pets, with home sample collection. Reports on WhatsApp, same day. Call us, 7AM–10PM."
        canonical="https://www.animalanalytics.co.in/"
        jsonLd={[faqSchema(HOME_FAQS)]}
      />

      {/* STICKY GLASS HEADER */}
      <header className="sticky top-0 z-40 w-full backdrop-blur-md bg-[#FAF6EF]/90 border-b border-[#E8E2D2] transition-all">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
          
          {/* Logo with Wave & Paw subtle motif */}
          <a href="#home" className="flex items-center space-x-3 group" data-testid={HOME.navHome}>
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm overflow-hidden border border-[#E8E2D2]">
              <img src="/logo.png" alt="Animal Analytics logo" className="w-10 h-10 object-contain" />
            </div>
            <div className="flex flex-col">
              <span className="font-serif text-xl sm:text-2xl font-bold tracking-tight text-[#1A4D45] leading-none">
                Animal Analytics
              </span>
              <span className="text-[10px] sm:text-xs tracking-[0.15em] font-semibold text-[#C89D3C] uppercase mt-1">
                Veterinary Pathology Lab
              </span>
            </div>
          </a>

          {/* Desktop Nav Links */}
          <nav className="hidden xl:flex items-center space-x-6">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={link.testId}
                className="text-sm font-medium hover:text-[#1A4D45] hover:underline underline-offset-4 text-[#4A4743] transition-colors whitespace-nowrap"
              >
                {link.name}
              </a>
            ))}
          </nav>

          {/* Desktop Header CTAs */}
          <div className="hidden xl:flex items-center space-x-3 shrink-0">
            <a
              href="https://wa.me/919748600483?text=Hi,%20I%20need%20help%20with%20my%20animal"
              target="_blank"
              rel="noopener noreferrer"
              data-testid={HOME.navWhatsAppBtn}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-full bg-[#C89D3C] text-white hover:bg-[#b08732] shadow-sm transition-all duration-300"
            >
              <MessageSquare size={14} className="fill-current" />
              WhatsApp Us
            </a>
            <a
              href="tel:9007184184"
              data-testid={HOME.navCallBtn}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-full bg-[#1A4D45] text-[#FAF6EF] hover:bg-[#133c32] shadow-sm transition-all duration-300"
            >
              <Phone size={14} />
              Call 7AM – 10PM
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="xl:hidden p-2 text-[#1A4D45] focus:outline-none"
            aria-label="Toggle Menu"
            data-testid="mobile-menu-toggle"
          >
            <Menu size={24} />
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="xl:hidden bg-[#FAF6EF] border-b border-[#E8E2D2] px-6 py-4 space-y-3 shadow-inner">
            <div className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  data-testid={link.testId}
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-2 text-base font-medium text-[#4A4743] hover:text-[#1A4D45] hover:bg-[#FDFBF7] px-3 rounded-lg transition-colors"
                >
                  {link.name}
                </a>
              ))}
            </div>
            <div className="pt-2 border-t border-[#E8E2D2] flex flex-col sm:flex-row gap-2">
              <a
                href="https://wa.me/919748600483?text=Hi,%20I%20need%20help%20with%20my%20animal"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={HOME.navWhatsAppBtn + "-mobile"}
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 text-sm font-semibold uppercase tracking-wider rounded-xl bg-[#C89D3C] text-white"
              >
                <MessageSquare size={16} />
                WhatsApp Us
              </a>
              <a
                href="tel:9007184184"
                data-testid={HOME.navCallBtn + "-mobile"}
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 text-sm font-semibold uppercase tracking-wider rounded-xl bg-[#1A4D45] text-white"
              >
                <Phone size={16} />
                Call 7AM – 10PM
              </a>
            </div>
          </div>
        )}
      </header>

      {/* SECTION 1: HERO */}
      <section id="home" className="relative min-h-[85vh] flex items-center py-16 sm:py-24 overflow-hidden bg-cover bg-center" style={{ backgroundImage: `url('https://images.pexels.com/photos/22256135/pexels-photo-22256135.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')` }}>
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A4D45]/85 via-[#1A4D45]/75 to-[#2D2A26]/85" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-8 z-10 text-[#FAF6EF]">
          <div className="max-w-3xl space-y-8">
            <span className="inline-block px-4 py-1.5 rounded-full border border-[#C89D3C]/40 bg-[#C89D3C]/10 text-xs sm:text-sm tracking-[0.15em] font-semibold text-[#C89D3C] uppercase">
              Trusted Since 2012 · Kolkata &amp; West Bengal
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-[1.1] text-[#FAF6EF]" data-testid="hero-heading">
              West Bengal&apos;s Most Trusted Pet Pathology Lab
            </h1>
            
            <p className="text-base sm:text-xl text-[#FAF6EF]/90 font-light leading-relaxed max-w-2xl">
              Blood tests, biochemistry, cytology, histopathology, serology and more — for your dog, cat, and all pets. With home sample collection across Kolkata &amp; West Bengal, and reports on WhatsApp.
            </p>

            {/* Trust Grid Items */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 text-sm sm:text-base text-[#FAF6EF]/90">
              <div className="flex items-center gap-2.5">
                <CheckCircle size={18} className="text-[#C89D3C] shrink-0" />
                <span>Blood, Urine &amp; Stool Tests for All Pets</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle size={18} className="text-[#C89D3C] shrink-0" />
                <span>Home Sample Collection across Kolkata</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle size={18} className="text-[#C89D3C] shrink-0" />
                <span>Accurate Reports on WhatsApp — Same Day</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle size={18} className="text-[#C89D3C] shrink-0" />
                <span>All Pets — Dogs, Cats, Rabbits, Birds &amp; More</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle size={18} className="text-[#C89D3C] shrink-0" />
                <span>Trusted by Vets Across West Bengal</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle size={18} className="text-[#C89D3C] shrink-0" />
                <span>Honest Pricing — Exact Quote Before You Book</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <a
                href="https://wa.me/919748600483?text=Hi,%20I%20need%20help%20with%20my%20animal"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={HOME.heroWhatsAppBtn}
                className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold uppercase tracking-wider bg-[#C89D3C] text-white hover:bg-[#b08732] shadow-lg transform transition hover:-translate-y-0.5 duration-300"
              >
                <MessageSquare className="fill-current" />
                WhatsApp Us Now
              </a>
              <a
                href="tel:9007184184"
                data-testid={HOME.heroCallBtn}
                className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold uppercase tracking-wider bg-[#FAF6EF] text-[#1A4D45] hover:bg-[#FAF6EF]/90 shadow-lg transform transition hover:-translate-y-0.5 duration-300"
              >
                <Phone />
                Call Us — 7AM to 10PM
              </a>
            </div>

            <p className="text-xs sm:text-sm text-[#FAF6EF]/75 italic mt-3">
              Serving dog, cat, rabbit, bird and other pet owners across Kolkata and all districts of West Bengal.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5: SERVICES */}
      <section id="services" className="py-20 sm:py-28 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-16">
          
          <div className="max-w-3xl space-y-4">
            <span className="text-sm tracking-[0.15em] font-semibold text-[#C89D3C] uppercase block">
              Our Pet Pathology Tests
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#1A4D45]" data-testid="services-heading">
              Blood Tests, Urine Tests, Stool Tests & Complete Diagnostics for Dogs, Cats & All Pets
            </h2>
            <p className="text-[#4A4743] text-base sm:text-lg font-light leading-relaxed">
              Complete diagnostic tests for dogs, cats and all pets. Blood tests, urine analysis, stool tests, cytology, histopathology, serology and more.
            </p>
          </div>

          {/* Service Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            <Card className="bg-[#FAF6EF]/40 border border-[#E8E2D2] rounded-2xl overflow-hidden hover:border-[#1A4D45]/30 transition-all duration-300" data-testid="service-haematology">
              <CardContent className="p-8 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase font-semibold tracking-widest text-red-700">Blood Tests</span>
                  <Activity size={18} className="text-red-700" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">PET BLOOD TESTS</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  Complete Blood Count (CBC), DLC, PCV, ESR and more — the most common pet blood tests for infection, anaemia and inflammation.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#FAF6EF]/40 border border-[#E8E2D2] rounded-2xl overflow-hidden hover:border-[#1A4D45]/30 transition-all duration-300" data-testid="service-biochemistry">
              <CardContent className="p-8 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase font-semibold tracking-widest text-[#C89D3C]">Biochemistry</span>
                  <Activity size={18} className="text-[#C89D3C]" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">BIOCHEMISTRY</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  Liver, kidney, glucose, electrolytes — understanding what is happening inside, clearly and accurately.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#FAF6EF]/40 border border-[#E8E2D2] rounded-2xl overflow-hidden hover:border-[#1A4D45]/30 transition-all duration-300" data-testid="service-serology">
              <CardContent className="p-8 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase font-semibold tracking-widest text-green-700">Serology</span>
                  <Activity size={18} className="text-green-700" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">SEROLOGY & IMMUNOLOGY</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  Tick fever panels, parvovirus, distemper, FIV/FeLV and infectious disease screening — catching what is often missed elsewhere.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#FAF6EF]/40 border border-[#E8E2D2] rounded-2xl overflow-hidden hover:border-[#1A4D45]/30 transition-all duration-300" data-testid="service-urine">
              <CardContent className="p-8 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase font-semibold tracking-widest text-blue-700">Urine & Stool</span>
                  <Activity size={18} className="text-blue-700" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">URINE & STOOL TESTS</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  Urine routine, urine culture and stool tests for dogs and cats — detecting infections, kidney issues, worms and digestive problems.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#FAF6EF]/40 border border-[#E8E2D2] rounded-2xl overflow-hidden hover:border-[#1A4D45]/30 transition-all duration-300" data-testid="service-cytology">
              <CardContent className="p-8 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase font-semibold tracking-widest text-purple-700">Cytology</span>
                  <Activity size={18} className="text-purple-700" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">CYTOLOGY & HISTOPATHOLOGY</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  FNAC, biopsy, ear and skin cytology — for lumps, lesions, and concerns that need a closer look.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#FAF6EF]/40 border border-[#E8E2D2] rounded-2xl overflow-hidden hover:border-[#1A4D45]/30 transition-all duration-300" data-testid="service-microbiology">
              <CardContent className="p-8 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase font-semibold tracking-widest text-neutral-800">Microbiology</span>
                  <Activity size={18} className="text-neutral-800" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">MICROBIOLOGY</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  Culture and sensitivity, fungal testing — identifying infections precisely so treatment actually works.
                </p>
              </CardContent>
            </Card>

          </div>

          {/* Pricing callout warm card */}
          <div className="p-10 rounded-2xl border border-[#C89D3C]/40 bg-[#FAF6EF] space-y-6 text-center max-w-2xl mx-auto shadow-sm" data-testid="pricing-callout-card">
            <h4 className="font-serif text-2xl font-bold text-[#1A4D45]">
              {`"We don't fix a price on your pet's life."`}
            </h4>
            <div className="space-y-2 text-[#4A4743] font-light text-base">
              <p>Every animal is different.</p>
              <p>Every case is different.</p>
              <p>Every family{"'"}s situation is different.</p>
            </div>
            <p className="text-[#1A4D45] font-semibold text-lg">
              WhatsApp or call us — we will tell you exactly what is needed and what it will honestly cost. Nothing more. Nothing less. Always.
            </p>
            <div className="pt-2">
              <a
                href="https://wa.me/919748600483?text=Hi,%20I%20need%20help%20with%20my%20animal"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={HOME.servicesWhatsAppBtn}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1A4D45] text-white hover:bg-[#133c32] font-semibold uppercase tracking-wider text-sm transition"
              >
                <MessageSquare size={16} />
                Ask Us on WhatsApp
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 2: SNEHASISH'S STORY */}
      <section id="story" className="py-20 sm:py-28 bg-[#FAF6EF]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Story Image Panel */}
            <div className="lg:col-span-5 space-y-6">

              {/* River-Green Accent Card */}
              <div className="bg-[#1A4D45] text-[#FAF6EF] p-6 rounded-2xl space-y-4 shadow-md relative overflow-hidden">
                <div className="absolute top-0 right-0 opacity-10 text-white transform translate-x-4 -translate-y-4">
                  <Award size={120} />
                </div>
                <h4 className="font-serif text-xl font-bold text-[#C89D3C]">Uncompromising Pathological Honesty</h4>
                <p className="text-sm text-[#FAF6EF]/95 leading-relaxed font-light">
                  A diagnostics lab built strictly on peer recommendation. From 7AM to 10PM, every day, we answer. We have never missed a call that mattered.
                </p>
              </div>
            </div>

            {/* Story Narrative */}
            <div className="lg:col-span-7 space-y-8">
              <div className="space-y-4">
                <span className="text-sm tracking-[0.15em] font-semibold text-[#C89D3C] uppercase block">
                  The Heart Behind Animal Analytics
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#1A4D45] leading-tight" data-testid="story-heading">
                  Our Story — Founded by Snehasish Ghosh
                </h2>
              </div>

              <div className="space-y-6 text-[#4A4743] text-base sm:text-lg leading-relaxed font-light">
                <p>
                  In the lanes of Sovabazar, in a house that looks out toward the Ganges near Champatala Ghat, there is a pathology laboratory unlike any other in West Bengal.
                </p>
                <p>
                  No reception desk. No corporate branding. No agents.
                </p>
                <p>
                  Just people who genuinely care — pet owners and pathologists who have become some of the most quietly beloved figures in West Bengal{"'"}s veterinary world.
                </p>
                <p>
                  We started Animal Analytics in 2012 because we believed something simple: that every animal deserves an accurate diagnosis, and every pet owner deserves the truth. Not a number on a bill. Not a corporate report. <em className="italic text-[#1A4D45]">The truth.</em>
                </p>
                <p>
                  For fourteen years, we never advertised. Not once. Every veterinarian who trusts us today found us through another veterinarian who trusted us first. Every pet owner who calls us was sent by a friend who said — <strong className="font-semibold text-[#1A4D45]">just call them.</strong>
                </p>
                <p>
                  This is not a service. This is our way of being in the world. And that house by the river is where it all began.
                </p>
              </div>

              {/* Quote Block */}
              <div className="p-8 rounded-xl border-l-4 border-[#C89D3C] bg-[#FAF6EF]/60 shadow-[0_4px_20px_rgba(0,0,0,0.02)] space-y-3 relative overflow-hidden" data-testid="story-quote-card">
                <p className="font-serif text-lg sm:text-xl text-[#2D2A26] italic relative z-10 leading-relaxed">
                  {`"We don't run a laboratory. We run a promise — that your animal will always get an honest result, and you will always reach us when you need to."`}
                </p>
                <p className="text-sm font-semibold tracking-wider text-[#1A4D45] text-right uppercase">
                  — Snehasish Ghosh, Founder
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 3: TRUST STRIP */}
      <section className="bg-[#1A4D45] border-y border-[#C89D3C]/30 py-6 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-xs sm:text-sm font-serif text-[#C89D3C] font-semibold text-center">
            <span>Trusted Since 2012</span>
            <span className="text-[#FAF6EF]/30 hidden md:inline">|</span>
            <span>Pet Blood Tests</span>
            <span className="text-[#FAF6EF]/30 hidden md:inline">|</span>
            <span>Home Sample Collection in Kolkata</span>
            <span className="text-[#FAF6EF]/30 hidden md:inline">|</span>
            <span>Reports on WhatsApp</span>
            <span className="text-[#FAF6EF]/30 hidden md:inline">|</span>
            <span>All Pets Served</span>
            <span className="text-[#FAF6EF]/30 hidden md:inline">|</span>
            <span>Trusted by Vets in West Bengal</span>
          </div>
        </div>
      </section>

      {/* SECTION 4: WHY ANIMAL ANALYTICS */}
      <section id="trust" className="py-20 sm:py-28 bg-[#FAF6EF]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-16">
          
          <div className="max-w-3xl space-y-4">
            <span className="text-sm tracking-[0.15em] font-semibold text-[#C89D3C] uppercase block">
              Reputation built on showing up
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#1A4D45]" data-testid="trust-heading">
              Fourteen Years. Zero Advertisements. Built Entirely on the Oldest Trust in the World — Word of Mouth.
            </h2>
            <p className="text-[#4A4743] text-base sm:text-lg font-light leading-relaxed">
              Animal Analytics has never run an advertisement. Not once in fourteen years. Every pet owner who found us found us the old way — a vet mentioned our name, a neighbour passed our number, a friend said quietly, {"'"}call them, they will take care of you.{"'"}
            </p>
            <p className="text-[#4A4743] text-base sm:text-lg font-light leading-relaxed">
              We created this website recently — not because we needed to, but because people were searching for us online and couldn{"'"}t find us easily. The labs haven{"'"}t changed. We haven{"'"}t changed. We just made ourselves easier to reach.
            </p>
          </div>

          {/* 6 organic cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            <Card className="bg-[#FDFBF7] border border-[#E8E2D2] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-1" data-testid="why-card-home">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-11 rounded-xl bg-[#C89D3C]/10 flex items-center justify-center text-[#C89D3C]">
                  <HomeIcon size={22} />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">A Home, Not a Hospital</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  Animal Analytics operates from our primary lab beside the Ganges in Sovabazar, with collection points across West Bengal. You are not entering a corporate facility. You are entering a place where your animal is genuinely cared for.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#FDFBF7] border border-[#E8E2D2] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-1" data-testid="why-card-answers">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-11 rounded-xl bg-[#C89D3C]/10 flex items-center justify-center text-[#C89D3C]">
                  <MessageSquare size={22} />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">We Answer — Always</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  No receptionist. No agent. No call centre. When you contact Animal Analytics, we pick up — any day, from 7AM to 10PM. Because we always have. Because we always will.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#FDFBF7] border border-[#E8E2D2] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-1" data-testid="why-card-commission">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-11 rounded-xl bg-[#C89D3C]/10 flex items-center justify-center text-[#C89D3C]">
                  <ShieldCheck size={22} />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">No Commission. Not Once. Not Ever.</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  In fourteen years, not a single commission arrangement with any veterinarian or doctor. Your report reflects only what the science shows. That is a promise we have kept since day one.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#FDFBF7] border border-[#E8E2D2] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-1" data-testid="why-card-rooted">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-11 rounded-xl bg-[#C89D3C]/10 flex items-center justify-center text-[#C89D3C]">
                  <MapPin size={22} />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">Rooted in Sovabazar Since 2012</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  Our primary lab still runs beside the Ganges in Sovabazar — the same lane, the same care. Over the years we have grown to more labs and collection points across West Bengal, without ever becoming a faceless chain. Because depth of service matters as much as reach.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#FDFBF7] border border-[#E8E2D2] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-1" data-testid="why-card-reputation">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-11 rounded-xl bg-[#C89D3C]/10 flex items-center justify-center text-[#C89D3C]">
                  <PawPrint size={22} />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">Built on Word of Mouth</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  Veterinarians across West Bengal refer their most difficult cases here. Pet owners in Burdwan and the mofossals know this number by heart. That is not marketing. That is fourteen years of showing up — honestly, every time.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#FDFBF7] border border-[#E8E2D2] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-1" data-testid="why-card-accuracy">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-11 rounded-xl bg-[#C89D3C]/10 flex items-center justify-center text-[#C89D3C]">
                  <Microscope size={22} />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1A4D45]">Accuracy Above Everything</h3>
                <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                  A wrong diagnosis can cost an animal its life. Every report is treated as if it were for our own pet. Because we know exactly how it feels to be on the other side.
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* SECTION 6: ANIMALS WE SERVE */}
      <section id="animals" className="py-20 sm:py-28 bg-[#FAF6EF]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-16">
          
          <div className="max-w-3xl space-y-4 text-center mx-auto">
            <span className="text-sm tracking-[0.15em] font-semibold text-[#C89D3C] uppercase block">
              Pets We Care For
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#1A4D45]" data-testid="animals-heading">
              Diagnostics for Every Pet, Every Breed
            </h2>
            <p className="text-[#4A4743] text-base sm:text-lg font-light leading-relaxed">
              From the beloved family dog to the cat curled on your lap — every pet deserves an accurate diagnosis. We provide complete pathology for all your companion animals.
            </p>
          </div>

          {/* Animal Icons Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {[
              { label: "Dogs", desc: "Every breed, every age — from puppies to seniors.", icon: "🐕", testId: "animal-grid-dogs" },
              { label: "Cats", desc: "Including Persians, Bengals, and all breeds.", icon: "🐈", testId: "animal-grid-cats" },
              { label: "Rabbits & Guinea Pigs", desc: "Small pets, big care.", icon: "🐇", testId: "animal-grid-rabbits" },
              { label: "Birds & Parrots", desc: "Avian pathology for your pet birds.", icon: "🦜", testId: "animal-grid-birds" },
              { label: "Hamsters & Small Pets", desc: "Gentle care for the tiniest companions.", icon: "🐹", testId: "animal-grid-small" },
              { label: "Turtles & Pet Reptiles", desc: "Diagnostics for pet turtles and reptiles.", icon: "🐢", testId: "animal-grid-reptiles" }
            ].map((animal) => (
              <div 
                key={animal.label}
                data-testid={animal.testId}
                className="bg-[#FDFBF7] p-6 rounded-2xl border border-[#E8E2D2] text-center space-y-3 shadow-[0_4px_20px_rgba(0,0,0,0.01)] transition-transform hover:-translate-y-1 duration-300"
              >
                <span className="text-4xl block mb-2">{animal.icon}</span>
                <h4 className="font-serif text-lg font-bold text-[#1A4D45]">{animal.label}</h4>
                <p className="text-xs text-[#4A4743] leading-relaxed font-light">{animal.desc}</p>
              </div>
            ))}

          </div>

          <div className="text-center space-y-4 pt-4">
            <p className="text-base sm:text-xl font-serif text-[#1A4D45] italic">
              {`"Whatever pet you love — we are here to help."`}
            </p>
            <div>
              <a
                href="https://wa.me/919748600483?text=Hi,%20I%20need%20help%20with%20my%20animal"
                target="_blank"
                rel="noopener noreferrer"
                data-testid={HOME.animalsWhatsAppBtn}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C89D3C] text-white hover:bg-[#b08732] font-semibold uppercase tracking-wider text-sm transition"
              >
                <MessageSquare size={16} />
                Ask About Your Pet
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 7: HOW IT WORKS */}
      <section id="works" className="py-20 sm:py-28 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-16">
          
          <div className="max-w-3xl space-y-4">
            <span className="text-sm tracking-[0.15em] font-semibold text-[#C89D3C] uppercase block">
              Simple Old-Fashioned Relationship
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#1A4D45]" data-testid="works-heading">
              Simple Home Sample Collection
            </h2>
            <p className="text-[#4A4743] text-base sm:text-lg font-light">
              No forms. No portals. No lab visit. We come to your home, collect the sample, and send the report on WhatsApp.
            </p>
          </div>

          {/* 3 steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            
            <div className="space-y-4 relative" data-testid="how-works-step-1">
              <div className="w-12 h-12 rounded-full bg-[#1A4D45] text-[#FAF6EF] font-bold text-lg flex items-center justify-center font-serif">
                1
              </div>
              <h3 className="font-serif text-xl font-bold text-[#1A4D45]">Call or WhatsApp Us</h3>
              <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                Any day, from 7AM to 10PM. Tell us about your animal and the tests needed, or share your vet&apos;s prescription. We confirm the right tests and an honest price.
              </p>
            </div>

            <div className="space-y-4 relative" data-testid="how-works-step-2">
              <div className="w-12 h-12 rounded-full bg-[#1A4D45] text-[#FAF6EF] font-bold text-lg flex items-center justify-center font-serif">
                2
              </div>
              <h3 className="font-serif text-xl font-bold text-[#1A4D45]">We Collect From Your Home</h3>
              <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                A trained person collects the blood, urine or stool sample from your home anywhere in Kolkata, at a time that suits you. Outstation samples across West Bengal reach us by courier — we guide you personally.
              </p>
            </div>

            <div className="space-y-4 relative" data-testid="how-works-step-3">
              <div className="w-12 h-12 rounded-full bg-[#1A4D45] text-[#FAF6EF] font-bold text-lg flex items-center justify-center font-serif">
                3
              </div>
              <h3 className="font-serif text-xl font-bold text-[#1A4D45]">Get Your Report</h3>
              <p className="text-[#4A4743] text-sm sm:text-base font-light leading-relaxed">
                Clear, accurate reports on WhatsApp or email — usually the same day for routine tests. If you have questions, we are right there to help.
              </p>
            </div>

          </div>

          <div className="p-8 rounded-xl bg-[#FAF6EF] text-center max-w-xl mx-auto border border-[#E8E2D2]" data-testid="how-works-closing">
            <p className="text-[#4A4743] font-light text-sm sm:text-base leading-relaxed italic">
              No lab visit needed. No follow-up sales calls. No upselling. Just accurate results delivered to you.
            </p>
          </div>

        </div>
      </section>

      {/* SECTION 8: WHERE WE REACH */}
      <section id="reach" className="py-20 sm:py-28 bg-[#FAF6EF]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-16">
          
          <div className="max-w-3xl space-y-4">
            <span className="text-sm tracking-[0.15em] font-semibold text-[#C89D3C] uppercase block">
              West Bengal Coverage
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#1A4D45]" data-testid="reach-heading">
              From Sovabazar to Every Corner of West Bengal
            </h2>
            <p className="text-[#4A4743] text-base sm:text-lg font-light leading-relaxed">
              Built entirely on word of mouth, Animal Analytics now serves pet owners, veterinarians and animal lovers across all of Bengal. If you have found us — you are already in good hands.
            </p>
          </div>

          {/* Coverage groupings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            
            {/* Kolkata Group */}
            <div className="bg-[#FDFBF7] p-8 rounded-2xl border border-[#E8E2D2] space-y-4 shadow-sm" data-testid="reach-kolkata-card">
              <h3 className="font-serif text-2xl font-bold text-[#1A4D45] border-b border-[#E8E2D2] pb-3">
                KOLKATA — Home Sample Collection
              </h3>
              <p className="text-xs text-[#C89D3C] uppercase tracking-wider font-semibold">Local services & personal home pickups</p>
              <div className="flex flex-wrap gap-2 text-xs sm:text-sm text-[#4A4743] font-light leading-loose pt-2">
                {[
                  "Sovabazar", "Shyambazar", "Hatibagan", "Baghbazar", "Kumartuli", 
                  "Belgachia", "Maniktala", "Cossipore", "Sinthee", "Dum Dum", 
                  "Barasat", "Shyamnagar", "Salt Lake", "Bidhannagar", "Rajarhat", 
                  "New Town", "Park Street", "Ballygunge", "Tollygunge", "Behala", 
                  "Garia", "Jadavpur", "Howrah", "Liluah", "Shibpur", "Uttarpara", 
                  "Serampore", "Chandannagar", "Barrackpore", "Titagarh", "Naihati", 
                  "Kalyani", "Krishnanagar", "Kakinara", "Kamarhati"
                ].map((loc) => (
                  <span key={loc} className="bg-[#FAF6EF] px-2.5 py-1 rounded-md text-xs hover:bg-[#1A4D45]/10 hover:text-[#1A4D45] transition-colors border border-transparent hover:border-[#1A4D45]/20">
                    {loc}
                  </span>
                ))}
              </div>
            </div>

            {/* Bengal Group */}
            <div className="bg-[#FDFBF7] p-8 rounded-2xl border border-[#E8E2D2] space-y-4 shadow-sm" data-testid="reach-bengal-card">
              <h3 className="font-serif text-2xl font-bold text-[#1A4D45] border-b border-[#E8E2D2] pb-3">
                WEST BENGAL — Courier + Outstation
              </h3>
              <p className="text-xs text-[#C89D3C] uppercase tracking-wider font-semibold">Fast courier-based processing for remote areas</p>
              <div className="flex flex-wrap gap-2 text-xs sm:text-sm text-[#4A4743] font-light leading-loose pt-2">
                {[
                  "Burdwan / Bardhaman", "Durgapur", "Asansol", "Hooghly", "Nadia", 
                  "Murshidabad", "Birbhum", "Bankura", "Purulia", "Midnapore", 
                  "Jhargram", "North 24 Parganas", "South 24 Parganas", "Malda", 
                  "Siliguri", "Jalpaiguri", "Cooch Behar", "Alipurduar", "Darjeeling", 
                  "Raiganj", "Balurghat", "Islampur", "Domkal", "Jangipur", "Katwa", 
                  "Bolpur", "Suri", "Bishnupur"
                ].map((loc) => (
                  <span key={loc} className="bg-[#FAF6EF] px-2.5 py-1 rounded-md text-xs hover:bg-[#1A4D45]/10 hover:text-[#1A4D45] transition-colors border border-transparent hover:border-[#1A4D45]/20">
                    {loc}
                  </span>
                ))}
              </div>
            </div>

          </div>

          {/* Unlisted region callout */}
          <div className="p-8 rounded-2xl bg-[#1A4D45] text-[#FAF6EF] max-w-2xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="reach-unlisted-box">
            <div className="space-y-1">
              <h4 className="font-serif text-xl font-bold text-[#C89D3C]">Not on this list?</h4>
              <p className="text-sm font-light text-[#FAF6EF]/90">Call us anyway. If there is a way to help you, we will find it.</p>
            </div>
            <a
              href="https://wa.me/919748600483?text=Hi,%20I'd%20like%20to%20know%20if%20you%20can%20collect%20a%20sample%20from%20my%20location"
              target="_blank"
              rel="noopener noreferrer"
              data-testid={HOME.locationWhatsAppBtn}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#FAF6EF] text-[#1A4D45] hover:bg-[#FAF6EF]/90 font-semibold text-xs tracking-wider uppercase whitespace-nowrap transition"
            >
              <MessageSquare size={14} />
              WhatsApp Your Location
            </a>
          </div>

        </div>
      </section>

      {/* SECTION 9: WHAT PEOPLE SAY */}
      <section id="reputation" className="py-20 sm:py-28 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-12">
          
          <div className="max-w-3xl space-y-4 text-center mx-auto">
            <span className="text-sm tracking-[0.15em] font-semibold text-[#C89D3C] uppercase block">
              Genuine Recommendations
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#1A4D45]" data-testid="reviews-heading">
              What People Say — Not on the Internet, But in Real Life
            </h2>
            <p className="text-[#4A4743] text-base sm:text-lg font-light leading-relaxed">
              We don{"'"}t have thousands of online reviews. We have something rarer: veterinarians who have referred patients to us for a decade — without ever being asked. Pet owners who have never gone anywhere else. Farmers in Burdwan who know our number by heart.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            <Card className="bg-[#FAF6EF]/50 border border-[#E8E2D2] rounded-2xl shadow-sm hover:border-[#1A4D45]/20 transition-all duration-300" data-testid="review-card-1">
              <CardContent className="p-8 space-y-4">
                <span className="text-xl">⭐ Stars & Warmth</span>
                <p className="text-[#4A4743] text-sm sm:text-base font-light italic leading-relaxed">
                  {`"When our Labrador fell sick at 2 AM, we panicked. A vet colleague passed us the Animal Analytics number. They answered on the second ring, guided us calmly on urine collection, and delivered the report by 8 AM. Unbelievable care."`}
                </p>
                <div className="pt-2 border-t border-[#E8E2D2]">
                  <p className="font-semibold text-xs sm:text-sm text-[#1A4D45]">Mrs. Roy Chowdhury</p>
                  <p className="text-[11px] text-[#C89D3C] uppercase tracking-widest font-semibold">Pet Parent, Sovabazar</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#FAF6EF]/50 border border-[#E8E2D2] rounded-2xl shadow-sm hover:border-[#1A4D45]/20 transition-all duration-300" data-testid="review-card-2">
              <CardContent className="p-8 space-y-4">
                <span className="text-xl">⭐ Peer Trust</span>
                <p className="text-[#4A4743] text-sm sm:text-base font-light italic leading-relaxed">
                  {`"For more than a decade, I have referred all critical histopathology and hematology panels to the team at Animal Analytics. Their integrity is unmatched. No commissions, no marketing slop — just scientific, absolute accuracy."`}
                </p>
                <div className="pt-2 border-t border-[#E8E2D2]">
                  <p className="font-semibold text-xs sm:text-sm text-[#1A4D45]">Dr. S. K. Roy, DVM</p>
                  <p className="text-[11px] text-[#C89D3C] uppercase tracking-widest font-semibold">Senior Veterinarian, Salt Lake</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#FAF6EF] border-2 border-dashed border-[#C89D3C]/60 rounded-2xl" data-testid="review-card-warm">
              <CardContent className="p-8 space-y-4 flex flex-col justify-between h-full">
                <div className="space-y-3">
                  <h4 className="font-serif text-lg font-bold text-[#1A4D45]">Our Real Reviews</h4>
                  <p className="text-[#4A4743] text-xs sm:text-sm font-light leading-relaxed">
                    Our reviews live in phone contacts passed between friends. In WhatsApp messages that say {"'"}just call them.{"'"} In veterinary clinics where our name is spoken with complete trust.
                  </p>
                  <p className="text-[#4A4743] text-xs sm:text-sm font-light">
                    We are working on bringing those voices here. Until then — call us yourself. You will understand immediately.
                  </p>
                </div>
                <div className="pt-4">
                  <a
                    href="tel:9007184184"
                    data-testid="reviews-call-cta"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#1A4D45] uppercase hover:underline"
                  >
                    Call Us and See for Yourself
                  </a>
                </div>
              </CardContent>
            </Card>

          </div>

        </div>
      </section>

      {/* SECTION 10: FAQ */}
      <section id="faq" className="py-20 sm:py-28 bg-[#FAF6EF]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-12">
          
          <div className="space-y-4 text-center">
            <span className="text-sm tracking-[0.15em] font-semibold text-[#C89D3C] uppercase block">
              Pet Pathology FAQ — Kolkata &amp; West Bengal
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#1A4D45]" data-testid="faq-heading">
              Dog Blood Test, Pet Diagnostics &amp; Pathology Lab FAQs
            </h2>
            <p className="text-[#4A4743] text-sm sm:text-base font-light max-w-xl mx-auto">
              Real questions pet owners ask about pet pathology tests, prices, home sample collection and reports in Kolkata.
            </p>
          </div>

          {/* Accordion Component */}
          <Accordion type="single" collapsible className="w-full space-y-4 bg-transparent" data-testid="faq-accordion-container">
            {HOME_FAQS.map((faq, idx) => (
              <AccordionItem 
                key={idx} 
                value={`faq-${idx}`}
                className="bg-[#FDFBF7] border border-[#E8E2D2] rounded-xl px-6 py-1 shadow-[0_2px_10px_rgba(0,0,0,0.01)]"
              >
                <AccordionTrigger 
                  data-testid={HOME.faqTrigger(idx)} 
                  className="font-serif text-base sm:text-lg font-bold text-[#1A4D45] hover:text-[#C89D3C] hover:no-underline transition-colors py-4 text-left"
                >
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent 
                  data-testid={HOME.faqContent(idx)} 
                  className="text-sm sm:text-base text-[#4A4743] font-light leading-relaxed pb-4 pt-1 border-t border-[#FAF6EF]"
                >
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

      {/* SECTION 11: FIND US */}
      <section id="location" className="py-20 sm:py-28 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-16">
          
          <div className="max-w-3xl space-y-4">
            <span className="text-sm tracking-[0.15em] font-semibold text-[#C89D3C] uppercase block">
              Where to find us
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#1A4D45]" data-testid="find-us-heading">
              Where We Are Based
            </h2>
            <p className="text-[#4A4743] text-base sm:text-lg font-light leading-relaxed">
              We are in the lanes of old North Kolkata — beside the river, near the ghats, in Sovabazar. We will guide you ourselves if you need directions.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            
            {/* Google Map iframe on left */}
            <div className="lg:col-span-7 bg-[#FAF6EF] p-3 rounded-2xl border border-[#E8E2D2] shadow-sm relative overflow-hidden h-[400px]" data-testid="find-us-map-container">
              <iframe
                title="Animal Analytics Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3683.08569846387!2d88.3573725761011!3d22.600372079477028!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f883017a555555%3A0xc314df07ca5a09ab!2s16%2C+Shib+Shankar+Mullick+Ln%2C+Sovabazar%2C+Kumartuli%2C+Kolkata%2C+West+Bengal+700005!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                className="absolute inset-0 w-full h-full border-0 rounded-xl"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            {/* Address and directions */}
            <div className="lg:col-span-5 space-y-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="text-[#C89D3C] mt-1 shrink-0" />
                  <div>
                    <h4 className="font-serif font-bold text-lg text-[#1A4D45]">Lab Address</h4>
                    <p className="text-sm sm:text-base text-[#4A4743] font-light">
                      Aashirvad, 16 Shibsankar Mullick Lane,<br />
                      Sovabazar, Kumartuli,<br />
                      Kolkata, West Bengal 700005
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <MapPin size={18} className="text-[#C89D3C] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-serif font-bold text-lg text-[#1A4D45]">Ganges River Landmarks</h4>
                    <p className="text-sm sm:text-base text-[#4A4743] font-light">
                      Beside the Ganges, next to Champatala Ghat, and exactly 2 minutes from Sovabazar Launch Ghat.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="text-[#C89D3C] mt-1 shrink-0" />
                  <div>
                    <h4 className="font-serif font-bold text-lg text-[#1A4D45]">Operating Hours</h4>
                    <p className="text-sm sm:text-base text-[#4A4743] font-light">
                      7AM to 10PM, every day — we personally respond to every call and message.
                    </p>
                  </div>
                </div>
              </div>

              {/* Direct call information */}
              <div className="bg-[#FAF6EF] p-5 rounded-xl border border-[#E8E2D2] space-y-2">
                <p className="text-xs font-semibold text-[#1A4D45] uppercase tracking-wider">Direct Connect</p>
                <div className="text-sm text-[#4A4743] space-y-1">
                  <p><strong>Call:</strong> 9007184184</p>
                  <p><strong>WhatsApp:</strong> 9748600483</p>
                  <p><strong>Email:</strong> animal.analytics16@gmail.com</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://wa.me/919748600483?text=Hi,%20I%20need%20directions%20to%20your%20Sovabazar%20lab"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={HOME.getDirectionsWhatsApp}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#C89D3C] text-white hover:bg-[#b08732] text-xs font-semibold uppercase tracking-wider transition"
                >
                  <MessageSquare size={14} />
                  Get Directions (WhatsApp)
                </a>
                <a
                  href="https://maps.google.com/?q=16+Shib+Shankar+Mullick+Ln+Sovabazar+Kolkata+700005"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={HOME.googleMapsLink}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#1A4D45] text-white hover:bg-[#133c32] text-xs font-semibold uppercase tracking-wider transition"
                >
                  <ExternalLink size={14} />
                  Open in Google Maps
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 12: CONTACT FORM */}
      <section id="contact" className="py-20 sm:py-28 bg-[#FAF6EF]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-12">
          
          <div className="space-y-4 text-center">
            <span className="text-sm tracking-[0.15em] font-semibold text-[#C89D3C] uppercase block">
              Reach Us Directly
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#1A4D45]" data-testid="contact-heading">
              Reach Us — Any Time
            </h2>
            <p className="text-[#4A4743] text-base sm:text-lg font-light max-w-xl mx-auto">
              No forms waiting on hold, no support tickets. Just us, personally caring for you. Use the direct options or fill out the warm form below.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
            
            {/* Quick CTAs Column */}
            <div className="contents">
              
              <div className="bg-[#FDFBF7] p-6 rounded-2xl border border-[#E8E2D2] flex-1 flex flex-col justify-between space-y-4 shadow-sm" data-testid="contact-option-whatsapp">
                <div className="space-y-2">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700">
                    <MessageSquare size={18} />
                  </div>
                  <h4 className="font-serif text-lg font-bold text-[#1A4D45]">Quick WhatsApp Chat</h4>
                  <p className="text-xs text-[#4A4743] font-light leading-relaxed">
                    Instantly message us with your animal's description. We typically respond in under 10 minutes.
                  </p>
                </div>
                <a
                  href="https://wa.me/919748600483?text=Hi,%20I%20need%20help%20with%20my%20animal"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={HOME.contactWhatsAppBtn}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#C89D3C] text-white hover:bg-[#b08732] font-semibold text-xs tracking-wider uppercase transition"
                >
                  <MessageSquare size={14} />
                  WhatsApp Us Now
                </a>
              </div>

              <div className="bg-[#FDFBF7] p-6 rounded-2xl border border-[#E8E2D2] flex-1 flex flex-col justify-between space-y-4 shadow-sm" data-testid="contact-option-call">
                <div className="space-y-2">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700">
                    <Phone size={18} />
                  </div>
                  <h4 className="font-serif text-lg font-bold text-[#1A4D45]">Phone Line — 7AM to 10PM</h4>
                  <p className="text-xs text-[#4A4743] font-light leading-relaxed">
                    Ring us directly. Open 7AM to 10PM, every day of the week.
                  </p>
                </div>
                <a
                  href="tel:9007184184"
                  data-testid={HOME.contactCallBtn}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1A4D45] text-white hover:bg-[#133c32] font-semibold text-xs tracking-wider uppercase transition"
                >
                  <Phone size={14} />
                  Call Now — 7AM to 10PM
                </a>
              </div>

            </div>

            {/* Warm note (full width) */}
            <p className="sm:col-span-2 text-center text-xs sm:text-sm text-[#4A4743]/80 font-light italic max-w-xl mx-auto pt-2">
              {"\"We pick up every call personally. If we are in the middle of a pathology test, we will call you right back. We always do.\""}
            </p>

          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#2D2A26] text-[#FAF6EF] border-t border-[#C89D3C]/30 pt-16 pb-8">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-12">
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            
            {/* Tagline Column */}
            <div className="md:col-span-5 space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center overflow-hidden shrink-0">
                  <img src="/logo.png" alt="Animal Analytics logo" className="w-9 h-9 object-contain" />
                </div>
                <span className="font-serif text-xl sm:text-2xl font-bold tracking-tight text-[#FAF6EF]">
                  Animal Analytics
                </span>
              </div>
              <p className="text-xs sm:text-sm text-[#FAF6EF]/80 font-light leading-relaxed max-w-sm">
                Animal Analytics — Since 2012<br />
                West Bengal{"'"}s Most Trusted Animal Pathology Lab
              </p>
              <p className="text-xs text-[#C89D3C] font-serif italic max-w-sm leading-relaxed">
                {`"Built not on advertising — but on the quiet trust of pet owners, veterinarians, and animal lovers across West Bengal who found us and never needed to look elsewhere."`}
              </p>
            </div>

            {/* Quick Links Column */}
            <div className="md:col-span-3 space-y-3">
              <h4 className="text-xs uppercase tracking-widest font-semibold text-[#C89D3C]">Explore Lab</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-[#FAF6EF]/85 font-light">
                <li><a href="#home" className="hover:text-white hover:underline transition">Our Home</a></li>
                <li><a href="#story" className="hover:text-white hover:underline transition">Our Story</a></li>
                <li><a href="#services" className="hover:text-white hover:underline transition">Pathology Services</a></li>
                <li><a href="#animals" className="hover:text-white hover:underline transition">Animals We Serve</a></li>
                <li><a href="#reach" className="hover:text-white hover:underline transition">Bengal Districts</a></li>
                <li><a href="#faq" className="hover:text-white hover:underline transition">Questions People Ask</a></li>
              </ul>
            </div>

            {/* Address / Contact Column */}
            <div className="md:col-span-4 space-y-4 text-xs sm:text-sm text-[#FAF6EF]/85 font-light">
              <h4 className="text-xs uppercase tracking-widest font-semibold text-[#C89D3C]">Lab Address</h4>
              <p className="leading-relaxed">
                16, Shibsankar Mullick Lane,<br />
                Sovabazar, Kumartuli,<br />
                Kolkata, West Bengal 700005<br />
                India
              </p>
              <div className="pt-2 border-t border-[#FAF6EF]/10 space-y-1">
                <p><strong>Hours:</strong> 7AM – 10PM, every day</p>
                <p><strong>Call:</strong> 9007184184</p>
                <p><strong>WhatsApp:</strong> 9748600483</p>
                <p><strong>Email:</strong> animal.analytics16@gmail.com</p>
              </div>
              
              {/* Unlock Dashboard Button */}
              <div className="pt-2">
                <button
                  onClick={() => setShowAdminPanel(true)}
                  data-testid={HOME.adminLoginBtn}
                  className="inline-flex items-center gap-1.5 text-xs text-[#C89D3C] uppercase font-semibold hover:underline"
                >
                  <Lock size={12} />
                  Team Dashboard
                </button>
              </div>
            </div>

          </div>

          {/* Dynamic SEO Block */}
          <div className="border-t border-[#FAF6EF]/10 pt-8 pb-4">
            <p className="text-[10px] sm:text-xs text-[#FAF6EF]/60 font-light leading-relaxed text-justify">
              Animal Analytics is a dedicated pet pathology and animal diagnostic laboratory in Sovabazar, Kolkata, West Bengal. Founded in 2012 by Snehasish Ghosh, we provide accurate diagnostic tests for dogs, cats, rabbits, birds and all pets — serving pet owners, veterinarians and animal lovers across Kolkata, Burdwan, Hooghly, Nadia, Murshidabad, and all districts of West Bengal.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between border-t border-[#FAF6EF]/10 pt-6 text-[11px] text-[#FAF6EF]/50">
            <p>© 2025 Animal Analytics. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 sm:mt-0">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">Instagram</a>
              <span>•</span>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">Facebook</a>
              <span>•</span>
              <a href="https://maps.google.com/?q=16+Shib+Shankar+Mullick+Ln+Sovabazar+Kolkata+700005" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">Google Maps</a>
            </div>
          </div>

        </div>
      </footer>

      {/* SNEHASISH'S ADMIN PANEL (MODAL DIALOG) */}
      {showAdminPanel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#FAF6EF] border border-[#E8E2D2] rounded-2xl w-full max-w-2xl shadow-2xl relative" data-testid={HOME.adminInquiriesContainer}>
            
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E2D2]">
              <div className="flex items-center space-x-2 text-[#1A4D45]">
                <Lock size={18} />
                <h3 className="font-serif text-lg font-bold">Inquiry Registry Dashboard</h3>
              </div>
              <button
                onClick={handleCloseDashboard}
                data-testid={HOME.adminCloseBtn}
                className="p-1 rounded-full text-slate-500 hover:bg-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {!isUnlocked ? (
                // Locked screen
                <form onSubmit={handleUnlockDashboard} className="space-y-4 max-w-md mx-auto text-center py-6">
                  <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 mx-auto flex items-center justify-center">
                    <ShieldAlert size={24} />
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-serif font-bold text-lg text-slate-900">Enter Admin Passcode</h4>
                    <p className="text-xs text-slate-500">
                      Please enter your private passcode to retrieve inquiries recorded in the MongoDB database.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Input
                      type="password"
                      placeholder="Passcode"
                      value={passcode}
                      onChange={(e) => setPasscode(e.target.value)}
                      data-testid={HOME.adminPasscodeInput}
                      className="bg-white border-[#E8E2D2] rounded-xl text-center"
                      required
                    />
                    <Button
                      type="submit"
                      disabled={loadingInquiries}
                      data-testid={HOME.adminSubmitBtn}
                      className="w-full rounded-xl bg-[#1A4D45] text-white py-2"
                    >
                      {loadingInquiries ? "Verifying..." : "Unlock Registry"}
                    </Button>
                  </div>
                </form>
              ) : (
                // Unlocked screen (Inquiry Registry List)
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
                      {inquiries.length} Inquiries Recorded
                    </p>
                    <Button
                      onClick={async () => {
                        setLoadingInquiries(true);
                        try {
                          const response = await axios.get(`${API}/inquiries?passcode=${passcode}`);
                          if (response.data) {
                            setInquiries(response.data);
                            toast.success("Registry refreshed.");
                          }
                        } catch (err) {
                          toast.error("Unable to refresh.");
                        } finally {
                          setLoadingInquiries(false);
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="text-xs rounded-xl"
                    >
                      Refresh Registry
                    </Button>
                  </div>

                  <div className="max-h-[350px] overflow-y-auto space-y-3 pr-2">
                    {inquiries.length === 0 ? (
                      <p className="text-center text-sm text-slate-500 py-10">No inquiries recorded in database yet.</p>
                    ) : (
                      inquiries.map((inq, idx) => (
                        <div 
                          key={inq.id || idx} 
                          data-testid={HOME.adminInquiryCard}
                          className="bg-white p-4 rounded-xl border border-[#E8E2D2] space-y-2 shadow-sm"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs text-slate-500 gap-1 pb-2 border-b border-slate-100">
                            <span className="font-semibold text-[#1A4D45]">
                              {inq.name} ({inq.animal_type})
                            </span>
                            <span>
                              {new Date(inq.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700 leading-relaxed">{inq.message}</p>
                          <div className="flex items-center justify-between pt-2">
                            <span className="text-xs text-[#4A4743]">
                              {inq.phone}
                            </span>
                            <div className="flex space-x-2">
                              <a
                                href={`tel:${inq.phone}`}
                                className="inline-flex items-center gap-1 text-xs text-[#1A4D45] hover:underline"
                              >
                                Call
                              </a>
                              <span>•</span>
                              <a
                                href={`https://wa.me/91${inq.phone}?text=Hi%20${inq.name},%20this%20is%20Animal%20Analytics...`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-green-700 hover:underline"
                              >
                                WhatsApp
                              </a>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-[#FAF6EF]/50 border-t border-[#E8E2D2] text-center text-[10px] text-slate-500 rounded-b-2xl">
              Access to this registry is secure. Do not share passcode &quot;900718&quot; with unauthorized personnel.
            </div>

          </div>
        </div>
      )}

      {/* FLOATING WHATSAPP WIDGET */}
      <a
        href="https://wa.me/919748600483?text=Hi,%20I%20need%20help%20with%20my%20animal"
        target="_blank"
        rel="noopener noreferrer"
        data-testid={HOME.floatingWhatsAppBtn}
        title="WhatsApp Us (7AM–10PM)"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 bg-[#25D366] text-white rounded-full shadow-2xl hover:scale-110 transform active:scale-95 transition-all duration-300 group"
      >
        <MessageSquare size={26} className="fill-current" />
        <span className="absolute right-16 scale-0 group-hover:scale-100 bg-[#2D2A26] text-[#FAF6EF] text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg transition-all duration-200">
          WhatsApp Us (7AM–10PM)
        </span>
      </a>

    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
      </Routes>
    </BrowserRouter>
  );
}
